import { ReactElement, ReactNode } from 'react'
import { Paper, Stack, SxProps } from '@mui/material'
import { VerticalDivider } from '@ui/structure'

interface Props {
  children: ReactNode
  sx?: SxProps
  /**
   * Only used with `placement="bottom"`. Set to a theme spacing index, a CSS length,
   * or a `calc(...)` string, e.g. to sit above a bottom panel.
   */
  offsetBottom?: number | string
  /** Optional full-width element rendered flush along the bottom edge (e.g. a progress bar). */
  footer?: ReactNode
}

const Toolbar = ({ children, sx, offsetBottom, footer }: Props): ReactElement => {
  return (
    <Stack
      direction="row"
      spacing={0}
      sx={[
        theme => ({
          position: 'absolute',
          display: 'flex',
          zIndex: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          bottom:
            offsetBottom !== undefined
              ? typeof offsetBottom === 'string'
                ? offsetBottom
                : theme.spacing(offsetBottom)
              : theme.spacing(3),
        }),
        ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Paper
        elevation={0}
        sx={theme => ({
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.common.white,
          boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
          pointerEvents: 'auto',
          // Clip the footer (e.g. progress bar) to the rounded corners.
          overflow: footer ? 'hidden' : undefined,
          '@media (min-width: 1920px)': {
            '& .MuiToggleButton-root .MuiSvgIcon-root': { fontSize: 20 },
            '& .MuiIconButton-root .MuiSvgIcon-root': { fontSize: 20 },
          },
        })}
      >
        {/* Each top-level child is a group; groups are separated by a vertical divider. */}
        <Stack
          direction="row"
          spacing={0}
          alignItems="center"
          divider={<VerticalDivider />}
          sx={{ px: 0.5, py: 0.5 }}
        >
          {children}
        </Stack>
        {footer}
      </Paper>
    </Stack>
  )
}

export default Toolbar
